import axios, { AxiosError, type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';
import type { ApiErrorBody } from '@s-blog/shared-types';

// eng-review 1.1 — credentials:include for httpOnly cookies
const baseURL = import.meta.env.VITE_API_BASE_URL ?? '/api';

export const http: AxiosInstance = axios.create({
  baseURL,
  withCredentials: true,
  timeout: 20_000,
});

/**
 * Single-flight refresh to prevent multiple concurrent refreshes
 * (test plan requirement: "刷新期间并发请求不重复刷").
 */
let refreshInFlight: Promise<void> | null = null;

async function refreshOnce(): Promise<void> {
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = axios
    .post(`${baseURL}/auth/refresh`, null, { withCredentials: true })
    .then(() => undefined)
    .finally(() => {
      refreshInFlight = null;
    });
  return refreshInFlight;
}

http.interceptors.response.use(
  (r) => r,
  async (err: AxiosError<ApiErrorBody>) => {
    const original = err.config as InternalAxiosRequestConfig & { _retried?: boolean };

    // Auto-refresh once on 401 (except for /auth/refresh itself and /auth/login)
    if (
      err.response?.status === 401 &&
      !original._retried &&
      original.url !== '/auth/refresh' &&
      original.url !== '/auth/login'
    ) {
      original._retried = true;
      try {
        await refreshOnce();
        return http.request(original);
      } catch {
        // refresh failed → bubble up
      }
    }
    return Promise.reject(err);
  },
);

/** Convenience: extract a human-readable error message from any caught error. */
export function apiErrorMessage(err: unknown, fallback = 'Request failed'): string {
  if (axios.isAxiosError(err)) {
    const body = err.response?.data as ApiErrorBody | undefined;
    return body?.message ?? err.message ?? fallback;
  }
  if (err instanceof Error) return err.message;
  return fallback;
}
